if (!customElements.get('sticky-atc-v2')) {
  customElements.define('sticky-atc-v2', class StickyAtcV2 extends HTMLElement {
    constructor() {
      super();
      this.productSection = null;
      this.atcButton = null;
      this.modal = null;
      this.confirmBtn = null;
      this.changeBtn = null;
      this.backdrop = null;
      this.selectionDisplay = null;
      this.hasSizeOption = false;
    }

    connectedCallback() {
      this.productSection = document.querySelector('.product-v2');
      this.atcButton = this.querySelector('.product-v2-sticky-bar__atc-btn');
      this.modal = document.querySelector('.product-v2-size-modal');
      this.confirmBtn = document.querySelector('.product-v2-size-modal__confirm');
      this.changeBtn = document.querySelector('.product-v2-size-modal__change');
      this.backdrop = document.querySelector('.product-v2-size-modal__backdrop');
      this.selectionDisplay = document.querySelector('.product-v2-size-modal__selection');

      if (!this.atcButton) return;

      // Detect if product has a size option (more than 1 value)
      this.hasSizeOption = this._detectSizeOption();

      // Find the real ATC button in the product form. No normal button (custom /
      // request products, forms without add to cart) = no sticky bar at all.
      const realAtcBtn = document.querySelector('.product-v2 product-form button[name="add"]');
      if (!realAtcBtn) { this.classList.remove('show'); this.classList.add('hidden'); return; }
      const observeTarget = realAtcBtn;
      const notifyBtn = document.querySelector('.product-v2 #out-of-stock, .product-v2 .klaviyo-bis-trigger');

      // The bar only makes sense while the real button can be used: not when the
      // variant is sold out / coming soon (the "notify me" button is shown
      // instead), disabled, or hidden.
      const canBuy = () => {
        if (realAtcBtn.disabled || realAtcBtn.getAttribute('aria-disabled') === 'true') return false;
        if (realAtcBtn.offsetParent === null) return false;
        if (notifyBtn && notifyBtn.offsetParent !== null) return false;
        return true;
      };

      // Show the sticky bar only once the real ATC button has been scrolled
      // PAST (it is above the viewport). While it is still further down the
      // page the bar would sit on top of the content and compete with it.
      const update = () => {
        const show = canBuy() && observeTarget.getBoundingClientRect().bottom <= 0;
        this.classList.toggle('show', show);
        this.classList.toggle('hidden', !show);
      };
      // variant change: the real button may become (un)available a moment later
      const formEl = realAtcBtn.closest('form');
      if (formEl) formEl.addEventListener('change', () => { setTimeout(update, 50); setTimeout(update, 600); });
      new IntersectionObserver(update, { threshold: 0 }).observe(observeTarget);
      // a jump from below the button to above it (back-to-top, anchor) skips the
      // observer, so also re-check on scroll
      let ticking = false;
      window.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => { ticking = false; update(); });
      }, { passive: true });

      // Click handler — different behavior based on size options
      this.atcButton.addEventListener('click', () => this._handleAtcClick());

      if (this.confirmBtn) {
        this.confirmBtn.addEventListener('click', () => this._confirmAndAdd());
      }
      if (this.changeBtn) {
        this.changeBtn.addEventListener('click', () => this._scrollToForm());
      }
      if (this.backdrop) {
        this.backdrop.addEventListener('click', () => this._hideModal());
      }
    }

    _detectSizeOption() {
      // Check for a "Size" option with more than 1 value
      const sizeFieldsets = document.querySelectorAll('.product-v2 .product-form__input');
      for (const fieldset of sizeFieldsets) {
        const legend = fieldset.querySelector('legend, label.form__label');
        if (!legend) continue;
        const label = legend.textContent.trim().toLowerCase().replace(/:$/, '');
        if (label === 'size' || label === 'maat') {
          const radios = fieldset.querySelectorAll('input[type="radio"]');
          const options = fieldset.querySelectorAll('select option');
          if (radios.length > 1 || options.length > 1) return true;
        }
      }
      return false;
    }

    _getForm() {
      return document.querySelector('product-form form[data-type="add-to-cart-form"]')
        || document.querySelector('product-form form');
    }

    _handleAtcClick() {
      const form = this._getForm();
      if (!form) return;

      if (!this.hasSizeOption) {
        // No size options — add directly to cart
        this._submitForm(form);
      } else {
        // Has size options — show confirmation modal
        this._showSizeModal(form);
      }
    }

    _showSizeModal(form) {
      if (!this.modal) return;

      // Find selected size
      let selectedSize = '';
      const sizeFieldsets = (this.productSection || document).querySelectorAll('.product-form__input');
      for (const fieldset of sizeFieldsets) {
        const legend = fieldset.querySelector('legend, label.form__label');
        if (!legend) continue;
        const label = legend.textContent.trim().toLowerCase().replace(/:$/, '');
        if (label === 'size' || label === 'maat') {
          const checked = fieldset.querySelector('input[type="radio"]:checked');
          const select = fieldset.querySelector('select');
          if (checked) selectedSize = checked.value;
          else if (select) selectedSize = select.value;
          break;
        }
      }

      if (this.selectionDisplay) {
        this.selectionDisplay.textContent = selectedSize || '';
      }

      this.modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }

    _hideModal() {
      if (!this.modal) return;
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    _confirmAndAdd() {
      this._hideModal();
      const form = this._getForm();
      if (form) this._submitForm(form);
    }

    _submitForm(form) {
      const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
      form.dispatchEvent(submitEvent);
    }

    _scrollToForm() {
      this._hideModal();
      const realBtn = document.querySelector('.product-v2 product-form button[name="add"]');
      if (realBtn) {
        realBtn.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  });
}

/* Avada Volume Discount enhancements: fix prices + inject product image stack */
(function() {
  /**
   * Single function to fix all Avada price formatting:
   * 1. € directly in front of the amount (€24,95), the same as the product price above
   * 2. Replace decimal period with comma (EU format: 12,34 instead of 12.34)
   * Runs on all Avada price elements + our injected savings lines; any € already
   * there is stripped first, so each amount ends up with exactly one.
   */
  function fixAvadaPrices() {
    document.querySelectorAll(
      '.product-v2 .AOV-Offer__DiscountPrice, .product-v2 .AOV-Offer__BasePrice, .product-v2 .Avada-Offer__PriceDiscount, .product-v2 .Avada-Offer__PriceDefault, .product-v2 .avada-savings-line strong'
    ).forEach(function(el) {
      el.childNodes.forEach(function(node) {
        if (node.nodeType === 3) {
          var t = node.textContent;
          var fixed = t.replace(/€\s*/g, '').replace(/(\d+)\.(\d{2})/g, '$1,$2');
          fixed = fixed.replace(/(\d)/, '€$1');
          if (fixed !== t) node.textContent = fixed;
        }
      });
      /* Mark as fixed so CSS can reveal it */
      if (!el.closest('.avada-prices-ready')) {
        var body = el.closest('.Avada-Volume__Body, .AOV-Offer__Body');
        if (body) body.classList.add('avada-prices-ready');
      }
    });
  }

  function getActiveImageSrc() {
    // the chosen variant's first photo (never the slide that happens to be on screen)
    if (window.sockeloenVariantFirstImage) {
      var first = window.sockeloenVariantFirstImage();
      if (first) return first.replace(/width=\d+/, 'width=80');
    }
    var activeItem = document.querySelector('.product-v2 .product__media-item.is-active img');
    if (!activeItem) activeItem = document.querySelector('.product-v2 .product__media-item:not([style*="display: none"]) img');
    if (!activeItem) activeItem = document.querySelector('.product-v2 .product__media-item img');
    if (!activeItem) return null;
    var src = activeItem.currentSrc || activeItem.src;
    if (src.indexOf('width=') !== -1) {
      src = src.replace(/width=\d+/, 'width=80');
    }
    return src;
  }

  /**
   * Derive the single-unit price to use as a reference for computing tier
   * quantities from price math (see injectImageStacks below). Uses the
   * tier with the lowest discount price, which is normally the plain
   * "1 pair" tier — that price IS the true per-unit price regardless of
   * locale, since numbers don't need translation the way text labels do.
   */
  function getUnitPrice(items) {
    var lowest = null;
    items.forEach(function(item) {
      var priceEl = item.querySelector('.Avada-Offer__PriceDiscount');
      if (!priceEl) return;
      var price = parseFloat(priceEl.textContent.replace(/[^\d,.]/g, '').replace(',', '.'));
      if (!isNaN(price) && (lowest === null || price < lowest)) lowest = price;
    });
    return lowest;
  }

  function injectImageStacks(root) {
    var items = (root || document).querySelectorAll('.product-v2 .Avada-Volume__Item');
    if (!items.length) return;

    var imgSrc = getActiveImageSrc();
    if (!imgSrc) return;

    var unitPrice = getUnitPrice(items);

    items.forEach(function(item) {
      // Skip if already injected
      if (item.querySelector('.avada-img-stack')) return;

      // Determine how many images to show. Tiers phrased as "3 + 1 free"
      // need TWO counts (3 bought + 1 free) shown as two separate groups
      // with a "+" between them.
      //
      // Primary method: derive both counts from the PRICES, which are
      // numeric and locale-independent — unlike the AOV app's own tier
      // label text, which for this offer's non-English translations
      // (confirmed live: German renders "+ 1 gratis" with the leading "3"
      // missing entirely, upstream of any theme code) can't be trusted to
      // contain a parseable "X + Y" pattern in every language. Falls back
      // to text parsing only if the price math isn't available/sane.
      var qtyEl = item.querySelector('.Avada-Volume__Info--TriggerQty');
      var qtyText = qtyEl ? qtyEl.textContent.trim() : '';
      var buyCount, freeCount;
      var originalPriceEl = item.querySelector('.Avada-Offer__PriceDefault');
      var discountPriceEl = item.querySelector('.Avada-Offer__PriceDiscount');
      var totalQty = null, paidQty = null;
      if (unitPrice && originalPriceEl && discountPriceEl) {
        var originalPrice = parseFloat(originalPriceEl.textContent.replace(/[^\d,.]/g, '').replace(',', '.'));
        var discountPrice = parseFloat(discountPriceEl.textContent.replace(/[^\d,.]/g, '').replace(',', '.'));
        if (!isNaN(originalPrice) && originalPrice > 0) totalQty = Math.round(originalPrice / unitPrice);
        if (!isNaN(discountPrice) && discountPrice > 0) paidQty = Math.round(discountPrice / unitPrice);
      }
      if (totalQty && paidQty && totalQty > paidQty && paidQty >= 1) {
        buyCount = Math.min(paidQty, 5);
        freeCount = Math.min(totalQty - paidQty, 5);
      } else {
        var plusMatch = qtyText.match(/(\d+)\s*\+\s*(\d+)/);
        if (plusMatch) {
          buyCount = Math.min(parseInt(plusMatch[1], 10), 5);
          freeCount = Math.min(parseInt(plusMatch[2], 10), 5);
        } else {
          var singleMatch = qtyText.match(/(\d+)/);
          buyCount = singleMatch ? Math.min(parseInt(singleMatch[1], 10), 5) : 1;
          freeCount = 0;
        }
      }

      function makeImg(overlap) {
        var img = document.createElement('img');
        img.src = imgSrc;
        img.alt = '';
        img.loading = 'lazy';
        img.className = 'avada-img-stack__img';
        if (overlap) img.style.marginLeft = '-10px';
        return img;
      }

      // Create the image stack container
      var stack = document.createElement('div');
      stack.className = 'avada-img-stack';

      for (var i = 0; i < buyCount; i++) {
        stack.appendChild(makeImg(i > 0));
      }

      if (freeCount > 0) {
        var plusSign = document.createElement('span');
        plusSign.className = 'avada-img-stack__plus';
        plusSign.textContent = '+';
        stack.appendChild(plusSign);
        for (var j = 0; j < freeCount; j++) {
          stack.appendChild(makeImg(j > 0));
        }
      }

      // Insert after the info section, before the price
      var content = item.querySelector('.Avada-Volume__Content');
      var info = item.querySelector('.Avada-Volume__Info');
      if (content && info) {
        info.parentNode.insertBefore(stack, info.nextSibling);
      }
    });
  }

  function updateImageStacks() {
    var imgSrc = getActiveImageSrc();
    if (!imgSrc) return;
    document.querySelectorAll('.product-v2 .avada-img-stack__img').forEach(function(img) {
      img.src = imgSrc;
    });
  }

  function injectRadioDots(root) {
    var items = (root || document).querySelectorAll('.product-v2 .Avada-Volume__Item');
    if (!items.length) return;

    items.forEach(function(item) {
      // Skip if already injected
      if (item.querySelector('.avada-radio-dot')) return;

      var content = item.querySelector('.Avada-Volume__Content, .Avada-Volume__Content--Horizontal');
      if (!content) return;

      var dot = document.createElement('div');
      dot.className = 'avada-radio-dot';
      // Insert as first child of content
      content.insertBefore(dot, content.firstChild);
    });
  }

  function fixBadgeText() {
    document.querySelectorAll('.product-v2 .Avada-VolumeBoxBadge').forEach(function(badge) {
      if (badge.dataset.enhanced) return;
      badge.dataset.enhanced = '1';
      /* Text comes from the section's data-uci-badge-text attribute,
         rendered via Liquid's `| t` filter (locales/*.json, "uci_sale.
         badge_text") so it's correctly translated per storefront
         language instead of hardcoded English. */
      var section = badge.closest('[data-uci-badge-text]');
      badge.textContent = (section && section.dataset.uciBadgeText) || 'Most popular';
    });

    /* The AOV app's own header title is configured inside the Avada app
       and may still contain "UCI" — scrub it here as a safety net until
       the app config itself is updated. Legal request from the UCI
       (2026-07): the UCI name and flag may not be shown. */
    document.querySelectorAll('.product-v2 .Avada-Volume__HeaderTitle-Text').forEach(function(title) {
      if (title.textContent.indexOf('UCI') !== -1) {
        title.textContent = title.textContent.replace(/\bUCI\s*/g, '').trim();
      }
    });
  }

  function enhanceTierLabels() {
    /* document.documentElement.lang reflects Shopify's actual active
       locale (set via request.locale.iso_code in theme.liquid) — more
       reliable than matching against the URL path, which breaks for
       markets that don't use a /xx/ prefix. */
    var lang = (document.documentElement.lang || 'en').toLowerCase();
    var isNl = lang.indexOf('nl') === 0;
    var isDe = lang.indexOf('de') === 0;
    var items = document.querySelectorAll('.product-v2 .Avada-Volume__Item');
    if (!items.length) return;

    var unitPrice = getUnitPrice(items);

    items.forEach(function(item) {
      var qtyEl = item.querySelector('.Avada-Volume__Info--TriggerQty');
      var discountTextEl = item.querySelector('.Avada-Volume__DiscountText');
      var discountPrice = item.querySelector('.Avada-Offer__PriceDiscount');
      var originalPrice = item.querySelector('.Avada-Offer__PriceDefault');
      var priceArea = item.querySelector('.Avada-Offer__Price');

      if (!qtyEl) return;
      var qtyText = qtyEl.textContent.trim();
      /* Prefer price math over text parsing for the same reason as
         injectImageStacks() — the AOV app's own tier label text can be
         missing its leading quantity number in non-English locales
         (confirmed: German renders "+ 1 gratis" with no "3"), which would
         otherwise misclassify a multi-pair bundle tier as qty===1 and
         apply the wrong (single-item) label logic below. */
      var qty = 1;
      if (unitPrice && discountPrice) {
        var dpForQty = parseFloat(discountPrice.textContent.replace(/[^\d,.]/g, '').replace(',', '.'));
        if (!isNaN(dpForQty) && dpForQty > 0) {
          var derivedQty = Math.round(dpForQty / unitPrice);
          if (derivedQty >= 1) qty = derivedQty;
        }
      } else {
        var qtyMatch = qtyText.match(/(\d+)/);
        qty = qtyMatch ? parseInt(qtyMatch[1], 10) : 1;
      }

      if (qty === 1) {
        if (discountTextEl && discountPrice && originalPrice) {
          var dp1 = parseFloat(discountPrice.textContent.replace(/[^\d,.]/g, '').replace(',', '.'));
          var op1 = parseFloat(originalPrice.textContent.replace(/[^\d,.]/g, '').replace(',', '.'));
          if (op1 > dp1) {
            var saved1 = (op1 - dp1).toFixed(2).replace('.', ',');
            var saleText = isNl ? '— Bespaar €' + saved1 : isDe ? '— Spare €' + saved1 : '— Save €' + saved1;
            if (discountTextEl.textContent.trim() !== saleText) {
              discountTextEl.textContent = saleText;
              discountTextEl.style.color = '#e45b30';
              discountTextEl.style.fontSize = '12px';
            }
            originalPrice.style.setProperty('display', 'block', 'important');
          } else {
            var stdText = isNl ? '— Standaardprijs' : isDe ? '— Standardpreis' : '— Standard price';
            if (discountTextEl.textContent.trim() !== stdText) {
              discountTextEl.textContent = stdText;
              discountTextEl.style.color = '#888';
              discountTextEl.style.fontSize = '12px';
            }
          }
        }
      } else if (qty > 1 && discountPrice) {
        // Multi-pair: add /pair label via UnitPriceLabel span (survives Avada re-renders)
        var unitLabel = discountPrice.querySelector('.AOV-Offer__UnitPriceLabel');
        var pairText = isNl ? ' /paar' : isDe ? ' /Paar' : ' /pair';
        if (unitLabel && unitLabel.textContent !== pairText) {
          unitLabel.textContent = pairText;
          unitLabel.className += ' avada-per-pair';
        }

        if (originalPrice) {
          originalPrice.style.setProperty('display', 'block', 'important');
        }

        // Calculate and show savings inside the price column (only inject once)
        if (originalPrice && !priceArea.querySelector('.avada-savings-line')) {
          var dp = parseFloat(discountPrice.textContent.replace(/[^\d,.]/g, '').replace(',', '.'));
          var op = parseFloat(originalPrice.textContent.replace(/[^\d,.]/g, '').replace(',', '.'));
          var totalSaved = (op - dp);
          if (totalSaved > 0) {
            var savingsEl = document.createElement('div');
            savingsEl.className = 'avada-savings-line';
            var savingsFormatted = totalSaved.toFixed(2).replace('.', ',');
            savingsEl.innerHTML = isNl
              ? 'Je bespaart <strong>€' + savingsFormatted + '</strong>'
              : isDe
              ? 'Du sparst <strong>€' + savingsFormatted + '</strong>'
              : 'You save <strong>€' + savingsFormatted + '</strong>';
            priceArea.appendChild(savingsEl);
          }
        }
      }
    });
  }

  function enhance() {
    fixAvadaPrices();
    injectRadioDots();
    injectImageStacks();
    fixBadgeText();
    enhanceTierLabels();
  }

  function needsEnhancement() {
    var items = document.querySelectorAll('.product-v2 .Avada-Volume__Item');
    if (!items.length) return false;
    // Check if any item is missing our injections
    for (var i = 0; i < items.length; i++) {
      if (!items[i].querySelector('.avada-radio-dot')) return true;
    }
    return false;
  }

  function init() {
    enhance();
    var container = document.querySelector('.product-v2');
    if (!container) return;

    /*
     * Poll instead of MutationObserver to avoid infinite loop.
     * Avada's own MutationObserver re-renders its tiles when we inject
     * elements, which would trigger our observer → enhance() → Avada
     * re-renders → observer → enhance() → infinite loop.
     * Polling every 800ms is safe and still feels responsive.
     */
    setInterval(function() {
      if (needsEnhancement()) enhance();
    }, 800);

    /* On variant change: fix Avada prices immediately + staggered delays to catch re-renders */
    var variantRadios = container.querySelector('variant-radios');
    if (variantRadios) {
      variantRadios.addEventListener('change', function() {
        fixAvadaPrices();
        setTimeout(function() { fixAvadaPrices(); updateImageStacks(); }, 50);
        setTimeout(fixAvadaPrices, 150);
        setTimeout(function() { fixAvadaPrices(); updateImageStacks(); }, 300);
        setTimeout(function() { fixAvadaPrices(); updateImageStacks(); }, 600);
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
